/**
 * /owner/receipt-claims — Owner Receipt Verification list.
 *
 * Phase R10.3 (2026-07-04) extension:
 *   - Sticky filter bar (date-preset + branch multi-select + status + flag)
 *   - 8-card counts strip
 *   - "Approve All Eligible" CTA → BulkApproveModal
 *   - Collapsible BranchPerformanceTable
 *   - Collapsible AutoApprovalToggle
 *   - Row-level dedup flag badge, branch pill, confidence, auto-approved
 *     indicator
 *
 * Preserved: existing status tabs, row-click → detail page, pagination.
 */
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Receipt, Clock, Hourglass, CheckCircle2, XCircle, ChevronRight,
  Loader2, Activity, Zap, ChevronDown, ChevronUp, MapPin,
} from 'lucide-react';

import { api } from '@/lib/api';
import { qk } from '@/lib/queryKeys';
import { useAuthStore } from '@/lib/auth';
import {
  PageHeader, SectionCard, FullSpinner, QueryError, EmptyState,
} from '@/components/ui_primitives/Misc';
import {
  formatRupiah, claimStatusLabel, rejectionReasonShort,
} from '@/lib/claimErrorUI';

import DedupFlagBadge from '@/components/owner/DedupFlagBadge';
import BranchFilter from '@/components/owner/BranchFilter';
import DatePresetFilter, { presetToDates } from '@/components/owner/DatePresetFilter';
import CountsStrip from '@/components/owner/CountsStrip';
import BranchPerformanceTable from '@/components/owner/BranchPerformanceTable';
import BulkApproveModal from '@/components/owner/BulkApproveModal';
import AutoApprovalToggle from '@/components/owner/AutoApprovalToggle';

const STATUS_OPTIONS = [
  { key: '',                      label: 'All',          Icon: Receipt },
  { key: 'pending_owner_review',  label: 'Pending',      Icon: Clock },
  { key: 'pending_ai_analysis',   label: 'Analyzing',    Icon: Hourglass },
  { key: 'approved',              label: 'Approved',     Icon: CheckCircle2 },
  { key: 'rejected',              label: 'Rejected',     Icon: XCircle },
];

const FLAG_OPTIONS = [
  { key: '',       label: 'All flags' },
  { key: 'green',  label: 'Clean' },
  { key: 'yellow', label: 'Review' },
  { key: 'orange', label: 'Potential Duplicate' },
  { key: 'red',    label: 'Confirmed Duplicate' },
];

export default function OwnerReceiptClaimsPage() {
  const token = useAuthStore((s) => s.token);
  const user = useAuthStore((s) => s.user);
  const navigate = useNavigate();
  const [sp, setSp] = useSearchParams();

  // ---- Filter state (URL-synced for shareable links) --------------
  const [status, setStatus] = useState(sp.get('status') ?? 'pending_owner_review');
  const [dedupFlag, setDedupFlag] = useState(sp.get('dedup_flag') ?? '');
  const [branchIds, setBranchIds] = useState(
    (sp.get('branch_ids') ?? '').split(',').filter(Boolean),
  );
  const [datePreset, setDatePreset] = useState({
    preset:    sp.get('date_preset') ?? 'all',
    date_from: sp.get('date_from') ?? null,
    date_to:   sp.get('date_to')   ?? null,
  });
  const [bulkOpen, setBulkOpen] = useState(false);
  const [perfExpanded, setPerfExpanded] = useState(true);
  const [autoApprovalExpanded, setAutoApprovalExpanded] = useState(false);

  // URL sync — write only changed keys so we don't trample unrelated params.
  useEffect(() => {
    const next = new URLSearchParams(sp);
    const setOrDelete = (k, v) => {
      if (v) next.set(k, v); else next.delete(k);
    };
    setOrDelete('status',      status);
    setOrDelete('dedup_flag',  dedupFlag);
    setOrDelete('branch_ids',  branchIds.join(','));
    setOrDelete('date_preset', datePreset.preset === 'all' ? '' : datePreset.preset);
    setOrDelete('date_from',   datePreset.date_from);
    setOrDelete('date_to',     datePreset.date_to);
    setSp(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, dedupFlag, branchIds.join(','), datePreset.preset, datePreset.date_from, datePreset.date_to]);

  // The list + counts + perf endpoints accept ONE branch_id (not multi).
  // When the user picks multiple, we show the merged view in the client
  // (merged list) but fire one query per selected branch. For simplicity,
  // the first phase ships single-select-equivalent semantics: if exactly
  // one branch is chosen, we pass it to the list/counts; if multi or
  // "unassigned" is mixed, we fall back to "no server filter" and
  // client-side filter the list. Branch-performance always sees the
  // full comma-list.
  const singleBranchId =
    branchIds.length === 1
      ? (branchIds[0] === 'unassigned' ? 'unassigned' : branchIds[0])
      : null;

  const listFilters = {
    status: status || undefined,
    dedup_flag: dedupFlag || undefined,
    branch_id: singleBranchId || undefined,
    date_from: datePreset.date_from || undefined,
    date_to:   datePreset.date_to   || undefined,
    limit: 100,
  };

  // ---- Queries ----------------------------------------------------
  const brandId = user?.brand_id ?? null;

  const branchesQ = useQuery({
    queryKey: qk.ownerBranchesForFilter(brandId),
    queryFn: () => api.ownerListBranches(token),
    enabled: !!token,
    staleTime: 60_000,
  });

  const claimsQ = useQuery({
    queryKey: qk.ownerReceiptClaims(listFilters),
    queryFn: () => api.ownerListReceiptClaims(token, listFilters),
    enabled: !!token,
    refetchInterval: status === 'pending_owner_review' ? 15_000 : false,
    refetchOnWindowFocus: true,
  });

  const countsQ = useQuery({
    queryKey: qk.ownerReceiptClaimsCounts({
      branch_id: singleBranchId,
      date_from: datePreset.date_from,
      date_to:   datePreset.date_to,
    }),
    queryFn: () => api.ownerReceiptClaimCounts(token, {
      branch_id: singleBranchId,
      date_from: datePreset.date_from,
      date_to:   datePreset.date_to,
    }),
    enabled: !!token,
    refetchInterval: 30_000,
  });

  const perfQ = useQuery({
    queryKey: qk.ownerReceiptClaimsBranchPerf({
      branch_ids: branchIds.join(','),
      date_from: datePreset.date_from,
      date_to:   datePreset.date_to,
    }),
    queryFn: () => api.ownerBranchPerformance(token, {
      branch_ids: branchIds.filter((b) => b !== 'unassigned').join(',') || undefined,
      date_from: datePreset.date_from || undefined,
      date_to:   datePreset.date_to   || undefined,
    }),
    enabled: !!token && perfExpanded,
    refetchInterval: 60_000,
  });

  const items = claimsQ.data?.items ?? [];
  const counts = countsQ.data ?? null;
  const eligibleForAuto = counts?.eligible_for_auto_approval ?? 0;

  // ---- Card-click to filter list ---------------------------------
  const onCountsCardClick = (key) => {
    switch (key) {
      case 'pending_owner_review':
      case 'pending_ai_analysis':
      case 'approved':
      case 'rejected':
        setStatus(key); setDedupFlag(''); break;
      case 'flag_red':
        setStatus('pending_owner_review'); setDedupFlag('red'); break;
      case 'flag_orange_yellow':
        setStatus('pending_owner_review'); setDedupFlag('orange'); break;
      case 'eligible_for_auto_approval':
        setStatus('pending_owner_review'); setDedupFlag('green'); break;
      case 'manual_review_required':
        setStatus('pending_owner_review'); setDedupFlag(''); break;
      default: break;
    }
  };

  const branchListForFilter = useMemo(() => {
    const raw = Array.isArray(branchesQ.data) ? branchesQ.data : [];
    return raw.map((b) => ({ id: b.id, name: b.name }));
  }, [branchesQ.data]);

  if (claimsQ.isLoading && !claimsQ.data) return <FullSpinner />;
  if (claimsQ.error) {
    return <QueryError error={claimsQ.error} onRetry={() => claimsQ.refetch()} />;
  }

  return (
    <div className="space-y-6" data-testid="owner-receipt-claims-page">
      <PageHeader
        kicker="Receipt Claims"
        title="Receipt Verification"
        subtitle="Review customer-uploaded receipts. Approving awards 1 stamp through the same loyalty engine as cashier QR scans."
      />

      {/* === Section 1 — Filter bar (sticky) === */}
      <div
        className="sticky top-0 z-20 -mx-1 px-1 py-2 bg-[#0B0B0F]/90 backdrop-blur border-b border-white/5"
        data-testid="receipt-claims-filter-bar"
      >
        <div className="flex items-center gap-2 flex-wrap">
          <DatePresetFilter value={datePreset} onChange={setDatePreset} />
          <BranchFilter
            branches={branchListForFilter}
            selected={branchIds}
            onChange={setBranchIds}
            loading={branchesQ.isLoading}
          />
          <select
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            data-testid="status-select"
            className="px-3 py-2 rounded-xl bg-[#15151B] border border-white/10 text-sm text-white min-h-[40px]"
          >
            {STATUS_OPTIONS.map((s) => (
              <option key={s.key || 'all'} value={s.key}>{s.label}</option>
            ))}
          </select>
          <select
            value={dedupFlag}
            onChange={(e) => setDedupFlag(e.target.value)}
            data-testid="dedup-flag-select"
            className="px-3 py-2 rounded-xl bg-[#15151B] border border-white/10 text-sm text-white min-h-[40px]"
          >
            {FLAG_OPTIONS.map((f) => (
              <option key={f.key || 'all'} value={f.key}>{f.label}</option>
            ))}
          </select>
        </div>
      </div>

      {/* === Section 2 — Counts strip === */}
      <CountsStrip
        counts={counts}
        isLoading={countsQ.isLoading}
        isError={!!countsQ.error}
        onCardClick={onCountsCardClick}
      />

      {/* === Section 3 — "Approve All Eligible" CTA === */}
      <div className="flex items-center justify-end">
        <button
          type="button"
          onClick={() => setBulkOpen(true)}
          disabled={eligibleForAuto === 0}
          data-testid="bulk-approve-cta"
          className={
            'inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold transition-colors ' +
            (eligibleForAuto > 0
              ? 'bg-gold text-black hover:bg-gold-light shadow-glow-gold-sm'
              : 'bg-white/5 text-white/40 cursor-not-allowed')
          }
        >
          <Zap size={14} />
          Approve All {eligibleForAuto} Eligible
        </button>
      </div>

      {/* === Section 4 — Branch Performance Dashboard === */}
      <SectionCard padded={false} testID="branch-performance-section">
        <div className="px-5 py-3 flex items-center justify-between border-b border-white/5">
          <div className="flex items-center gap-2">
            <MapPin size={14} className="text-gold" />
            <span className="text-sm font-semibold text-white">Branch Performance</span>
            {perfQ.data?.date_from ? (
              <span className="text-[11px] text-white/40 font-mono">
                {perfQ.data.date_from} → {perfQ.data.date_to}
              </span>
            ) : null}
          </div>
          <button
            type="button"
            onClick={() => setPerfExpanded((v) => !v)}
            data-testid="branch-performance-toggle"
            className="p-1 rounded-lg text-white/50 hover:text-white hover:bg-white/5"
          >
            {perfExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
          </button>
        </div>
        {perfExpanded ? (
          <BranchPerformanceTable
            rows={perfQ.data?.branches ?? []}
            isLoading={perfQ.isLoading}
            isError={!!perfQ.error}
            onBranchClick={(bid) => setBranchIds(bid === null ? ['unassigned'] : [bid])}
          />
        ) : null}
      </SectionCard>

      {/* === Section 5 — Auto-Approval settings === */}
      <SectionCard padded={false} testID="auto-approval-settings-section">
        <div className="px-5 py-3 flex items-center justify-between border-b border-white/5">
          <div className="flex items-center gap-2">
            <Zap size={14} className="text-gold" />
            <span className="text-sm font-semibold text-white">Auto-Approval Settings</span>
          </div>
          <button
            type="button"
            onClick={() => setAutoApprovalExpanded((v) => !v)}
            data-testid="auto-approval-settings-toggle"
            className="p-1 rounded-lg text-white/50 hover:text-white hover:bg-white/5"
          >
            {autoApprovalExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
          </button>
        </div>
        {autoApprovalExpanded && brandId ? (
          <div className="p-5">
            <AutoApprovalToggle brandId={brandId} />
          </div>
        ) : null}
      </SectionCard>

      {/* === Section 6 — Claims list (extended) === */}
      {items.length === 0 ? (
        <EmptyState
          icon={<Receipt size={20} />}
          title="No receipt claims match"
          subtitle="Try adjusting your filters."
          testID="owner-receipt-claims-empty"
        />
      ) : (
        <SectionCard padded={false} testID="owner-receipt-claims-list">
          <ul className="divide-y divide-white/5">
            {items.map((c) => (
              <ClaimRow
                key={c.id}
                claim={c}
                branches={branchListForFilter}
                onClick={() => navigate(`/owner/receipt-claims/${c.id}`)}
              />
            ))}
          </ul>
        </SectionCard>
      )}

      {claimsQ.isFetching && !claimsQ.isLoading ? (
        <div className="flex items-center justify-center gap-2 text-xs text-white/40">
          <Loader2 size={12} className="animate-spin" />
          Refreshing…
        </div>
      ) : null}

      <BulkApproveModal
        open={bulkOpen}
        onClose={() => setBulkOpen(false)}
        onCompleted={() => {
          claimsQ.refetch();
          countsQ.refetch();
          perfQ.refetch();
        }}
        branchFilter={singleBranchId}
      />
    </div>
  );
}

function ClaimRow({ claim, branches, onClick }) {
  const ai = claim.ai_extraction;
  const confidencePct = ai?.overall_confidence != null
    ? Math.round(ai.overall_confidence * 100)
    : null;
  const submitted = new Date(claim.created_at).toLocaleString('id-ID', {
    month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
  });
  const customerShort = claim.customer_user_id?.slice(0, 8) ?? '—';
  const branchName = claim.branch_id
    ? (branches.find((b) => b.id === claim.branch_id)?.name ?? 'Branch')
    : 'Unassigned';

  return (
    <li>
      <button
        type="button"
        onClick={onClick}
        data-testid={`owner-receipt-claims-row-${claim.id}`}
        className="w-full flex items-start gap-3 px-5 py-4 text-left hover:bg-white/[0.03] transition-colors"
      >
        <div className="w-10 h-10 rounded-xl bg-gold/10 border border-gold/30 grid place-items-center shrink-0">
          <Receipt size={16} className="text-gold" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-baseline justify-between gap-3">
            <div className="font-semibold text-white truncate">
              {ai?.brand_name ?? 'Receipt claim'}
            </div>
            <div className="text-xs text-white/40 font-mono shrink-0">
              {submitted}
            </div>
          </div>
          <div className="flex items-center gap-2 mt-1 flex-wrap text-xs text-white/55">
            <span className="font-mono">Customer #{customerShort}</span>
            {ai?.total_amount != null ? (
              <>
                <span className="opacity-40">·</span>
                <span className="font-semibold text-gold">
                  {formatRupiah(ai.total_amount)}
                </span>
              </>
            ) : null}
            {confidencePct != null ? (
              <>
                <span className="opacity-40">·</span>
                <span className={
                  'inline-flex items-center gap-1 ' +
                  (confidencePct >= 95 ? 'text-emerald-300'
                    : confidencePct >= 85 ? 'text-amber-300'
                    : 'text-red-300')
                }>
                  <Activity size={11} />
                  AI {confidencePct}%
                </span>
              </>
            ) : null}
            <span className="opacity-40">·</span>
            <span className="inline-flex items-center gap-1 text-white/40">
              <MapPin size={10} /> {branchName}
            </span>
            {claim.auto_approved ? (
              <>
                <span className="opacity-40">·</span>
                <span className="inline-flex items-center gap-1 text-emerald-300" title="Auto-approved">
                  <Zap size={11} /> Auto
                </span>
              </>
            ) : null}
          </div>
          <div className="mt-2 flex items-center gap-2 flex-wrap">
            <DedupFlagBadge
              flag={claim.dedup_flag}
              reason={claim.dedup_flag_reason}
              size="sm"
            />
          </div>
        </div>
        <StatusBadge status={claim.status} ownerDecision={claim.owner_decision} />
        <ChevronRight size={16} className="text-white/30 shrink-0 mt-2" />
      </button>
    </li>
  );
}

function StatusBadge({ status, ownerDecision }) {
  const map = {
    pending_ai_analysis:  { icon: Hourglass,    cls: 'bg-gold/15 text-gold border-gold/35' },
    pending_owner_review: { icon: Clock,        cls: 'bg-gold/15 text-gold border-gold/35' },
    approved:             { icon: CheckCircle2, cls: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/35' },
    rejected:             { icon: XCircle,      cls: 'bg-red-500/15 text-red-300 border-red-500/35' },
  };
  const m = map[status] ?? map.pending_owner_review;
  const Icon = m.icon;
  const label =
    status === 'rejected' && ownerDecision?.reason
      ? rejectionReasonShort(ownerDecision.reason)
      : claimStatusLabel(status);
  return (
    <span
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-[11px] font-semibold whitespace-nowrap shrink-0 ${m.cls}`}
    >
      <Icon size={12} />
      {label}
    </span>
  );
}
